import React, { useCallback, useState } from 'react';
import { useTranslation } from '@shared/core';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { Category } from '@graphql/generated';
import { Flex, TextV2, Box, BoxProps } from '@withjoy/joykit';
import { useLayout } from '@apps/guest/packages/layout-engine/layouts/LayoutProvider';
import { AccommodationCombined, AccommodationCombinedType } from '../Accommodations/Accommodation.types';
import { pxToRem } from '@withjoy/joykit/theme';
import { createDateFromUnformattedString } from '@shared/utils/dateHelpers';
import { SkeletonText, SkeletonThumbnail } from '@shared/components/Skeleton';
import { useFeatureValue, useIsFeatureEnabled } from '@shared/core/featureFlags';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import {
  body1ToParagraphOverride,
  hed2ToSubHeadingOverride,
  hed4ToSubHeadingOverride,
  label3ToParagraphOverride
} from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { useScaleGuestSiteFontSize } from '@apps/guest/components/GuestSiteTypographyOverride/hooks/useScaleGuestSiteFontSize';
import { TextEllipsisWrapper } from '@shared/utils/formatting/textEllipsis';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useAccommodationsCta } from '../../hooks/useAccommodationsCta';
import { DEFAULT_HOTEL_IMAGE, DEFAULT_HOTEL_IMAGE_WIDE } from '@shared/utils/constants';
import { EventVenue } from '@shared/utils/eventPlace';
import { useAccommodationsAnalyticsCompare } from '../../hooks/useAccommodationsAnalyticsCompare';
import { isInIframe } from '@shared/utils/isInIframe';
import { hasAccommodationLink } from '../Accommodations/Accommodation.utils';
import { useTextLineClampOverflow } from '@shared/hooks/useTextLineClampOverflow';
import { JoyPlacePricing } from './components/JoyPlacePricing';
import { JoyPlacePricingV2 } from './components/JoyPlacePricingV2';
import { RoomBlockPricingNonGrid } from './components/RoomBlockPricingNonGrid';
import { animationTransition } from '@shared/utils/animationTransition';
import { CTAButton } from './components/CTAButton';
import { ReservationsCount } from './components/ReservationsCount';
import { VenueAndTravelDistance } from './components/VenueAndTravelDistance';
import { PhotoCarousel } from '@shared/components/PhotoCarousel';
import { wrapPartnerHotelRedirect } from '../WrapPartnerHotelRedirect';
import { StarRating } from '@shared/components';

interface AccommodationsTileV2FavoriteProps {
  locationInfo?: EventVenue;
  eventHandle: string;
  isFirst: boolean;
  isLast: boolean;
  showDefaultData?: boolean;
  reservationCount: number;
  accommodation: AccommodationCombined;
  accommodationPropertyPhotos?: { url: string }[];
  setLocationBeingHovered?: (id: string | undefined) => void;
  onSelectAccommodation?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  from?: 'accom_page_grid' | 'hotel_map_list' | 'map_icon_modal';
}

const AccommodationsTileV2Favorite = wrapPartnerHotelRedirect((props: AccommodationsTileV2FavoriteProps) => {
  const { locationInfo, eventHandle, setLocationBeingHovered, accommodation, reservationCount, accommodationPropertyPhotos, onSelectAccommodation } = props;
  const { displayName, description, photo, cutoffDate, pricePerNight, id, interestedGuestCount, type, distance, starRating } = accommodation;

  const { layout } = useLayout();
  const { t } = useTranslation('guestSiteCustom');
  const accTrans = t('accommodations');
  const isMobile = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'sm2' }));
  const isMobileOrTablet = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'md' }));
  const { scaleFontSize } = useScaleGuestSiteFontSize();
  const inPreviewMode = isInIframe();

  const [fontPackExperimentEnabled] = useIsFeatureEnabled('fontPackExperiment');
  const [accommodationsMyOwnLinkEnabled] = useIsFeatureEnabled('accommodationsMyOwnLinkEnabled');
  const hasLink = hasAccommodationLink(accommodationsMyOwnLinkEnabled, accommodation.isCustomLinkEnabled, accommodation.customUrl, accommodation.url, accommodation.zhId);

  const isBrannan = layout === 'brannan';
  const isGridView = isBrannan && !isMobile;

  const isRoomBlock = type === AccommodationCombinedType.Roomblock;
  const { textRef, isOverflowing } = useTextLineClampOverflow(isRoomBlock ? 1 : 2);
  const [isCoupleNoteOpen, setIsCoupleNoteOpen] = useState(false);

  const { ctaTitle, ctaUrl } = useAccommodationsCta({ eventHandle, accommodation, locationInfo, from: 'hotel_map_list' });

  const { value: showPricingValue } = useFeatureValue('travelShowHotelPrice');
  const showPricingEnabled = showPricingValue === 'on';
  const showRegularHotelPricing = !accommodation.customUrl && showPricingEnabled && !inPreviewMode;

  const [showNewPriceComparisonUI] = useIsFeatureEnabled('enableAccommodationsPriceComparisonV2');

  useAccommodationsAnalyticsCompare({ accommodationPlaceId: id, shouldSkip: !!accommodation.customUrl || type === AccommodationCombinedType.Roomblock });

  const handleCoupleNoteOpen = useEventCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    setIsCoupleNoteOpen(isOpen => !isOpen);
  });

  const renderTagComponent = useCallback(() => {
    if (reservationCount) {
      return <ReservationsCount reservationsCount={reservationCount} />;
    } else if (interestedGuestCount && interestedGuestCount > 1) {
      <Flex borderRadius={pxToRem(20.632)} height={pxToRem(32)} alignItems="center" paddingX={4} backgroundColor="rgba(255, 255, 255, 0.5)" backdropFilter="blur(4px)">
        <GuestSiteTypographyOverride override={label3ToParagraphOverride} defaultFontFamily="Inter UI">
          <TextV2
            fontFamily={'Inter UI'}
            color={'black'}
            fontSize={scaleFontSize(pxToRem(13), Category.PARAGRAPH)}
            letterSpacing={pxToRem(-0.032)}
            fontWeight={600}
            alignSelf={'center'}
            typographyVariant="label3"
          >
            {accTrans.guestsInterested({ interestedGuestCount })}
          </TextV2>
        </GuestSiteTypographyOverride>
      </Flex>;
    }

    return null;
  }, [reservationCount, interestedGuestCount, scaleFontSize, accTrans]);

  return (
    <Flex
      position="relative"
      width={'100%'}
      justifyContent="center"
      padding={6}
      className="group"
      flexDirection="column"
      borderRadius={pxToRem(36)}
      {...(hasLink && { as: 'a', href: ctaUrl, onClick: onSelectAccommodation, target: '_blank' })}
      cursor={hasLink ? 'pointer' : 'default'}
      _hover={{
        backgroundColor: 'white',
        boxShadow:
          hasLink && !isMobile
            ? '0px -7px 27px 0px rgba(44, 41, 37, 0.06), -4px 0px 16px 0px rgba(0, 0, 0, 0.07), 4px 0px 16px 0px rgba(0, 0, 0, 0.07)'
            : '0px 7px 27px 0px rgba(44, 41, 37, 0.06), 0px 4px 16px 0px rgba(0, 0, 0, 0.07)'
      }}
      boxShadow="0px 0px 0px 0px rgba(44, 41, 37, 0), 0px 0px 0px 0px rgba(0, 0, 0, 0)"
      transition={animationTransition('box-shadow', {
        // slightly faster than normal
        duration: '300ms'
      })}
      onMouseEnter={() => {
        if (!isMobileOrTablet) {
          setLocationBeingHovered?.(id);
        }
      }}
      onMouseLeave={() => {
        if (!isMobileOrTablet) {
          setLocationBeingHovered?.(undefined);
        }
      }}
    >
      <Flex width={'100%'} position="relative">
        <Flex justifyContent="space-between" position="absolute" top={4} left={4} right={4} zIndex={1} flexWrap="wrap" gap={4}>
          <Box
            display={'flex'}
            justifyContent="center"
            alignItems="center"
            columnGap={2}
            width={32}
            height={32}
            borderRadius="50%"
            backgroundColor={'mono2'}
            backdropFilter={'blur(24px)'}
          >
            <GuestSiteTypographyOverride override={label3ToParagraphOverride} defaultFontFamily="Inter UI">
              <TextV2 typographyVariant="label3" fontSize={scaleFontSize(pxToRem(15), Category.PARAGRAPH)} fontWeight={500} fontFamily="Inter UI" color="mono14">
                1
              </TextV2>
            </GuestSiteTypographyOverride>
          </Box>
          {renderTagComponent()}
        </Flex>
        <Flex width={'100%'} flexDirection={'column'} gap={6}>
          <Box
            aspectRatio="3/2"
            width="100%"
            maxHeight={pxToRem(isGridView ? 324 : 206)}
            position={'relative'}
            backgroundColor={'mono2'}
            overflow={'hidden'}
            borderRadius={pxToRem(isGridView ? 24 : 12)}
          >
            <PhotoCarousel
              aspectRatio="3/2"
              images={
                accommodationPropertyPhotos?.map(photo => ({ src: photo?.url, alt: displayName || '' })) ?? [
                  { src: photo?.url || (isMobile ? DEFAULT_HOTEL_IMAGE : DEFAULT_HOTEL_IMAGE_WIDE), alt: displayName || '' }
                ]
              }
              navigation={{ variant: 'minimal', visible: 'default' }}
            />
          </Box>
          <Flex flexDirection={isGridView ? 'row' : 'column'} justifyContent={{ _: 'center', sm2: isBrannan ? 'space-between' : 'center' }} gap={6}>
            <Flex flexDirection="column" gap={7} flex={1}>
              <Flex flexDirection="column" gap={4}>
                <Flex flexDirection="column" gap={1}>
                  <GuestSiteTypographyOverride override={hed2ToSubHeadingOverride} defaultFontFamily="Inter UI">
                    <TextEllipsisWrapper
                      typographyVariant="hed2"
                      lineClamp={2}
                      {...(!fontPackExperimentEnabled && { fontFamily: 'Inter UI' })}
                      fontSize={scaleFontSize(pxToRem(22), Category.SUB_HEADING)}
                      fontWeight={600}
                      color="mono14"
                      lineHeight={'28.6px'}
                      letterSpacing={'-0.528px'}
                      title={displayName || ''}
                    >
                      {displayName}
                    </TextEllipsisWrapper>
                  </GuestSiteTypographyOverride>
                  <Flex gap={{ _: 1, sm2: 2 }} alignItems="center" flexWrap="wrap">
                    {!!starRating && <StarRating rating={starRating} id={accommodation.id} hideNonFilledStars />}
                    <VenueAndTravelDistance milesToVenue={distance} useShortWordingDistance={true} />
                  </Flex>
                  <Flex width="100%" gap={9} alignItems={isCoupleNoteOpen ? 'flex-start' : 'flex-end'} justifyContent="space-between">
                    {(description || (cutoffDate && isGridView)) && (
                      <Box>
                        {description && (
                          <Flex width="100%" alignItems="flex-end">
                            <GuestSiteTypographyOverride override={body1ToParagraphOverride} defaultFontFamily="Inter UI">
                              <TextEllipsisWrapper ref={textRef} lineClamp={isCoupleNoteOpen ? 'none' : isRoomBlock ? 1 : 2} lineHeight={1.5}>
                                <TextV2
                                  typographyVariant="body1"
                                  {...(!fontPackExperimentEnabled && { fontFamily: 'Inter UI' })}
                                  fontSize={scaleFontSize(pxToRem(15), Category.PARAGRAPH)}
                                  fontWeight={400}
                                  color="mono10"
                                  title={description}
                                  lineHeight={1.5}
                                >
                                  {description}
                                </TextV2>
                              </TextEllipsisWrapper>
                            </GuestSiteTypographyOverride>
                            {isOverflowing && (
                              <GuestSiteTypographyOverride override={body1ToParagraphOverride} defaultFontFamily="Inter UI">
                                <TextV2
                                  {...(!fontPackExperimentEnabled && { fontFamily: 'Inter UI' })}
                                  fontSize={scaleFontSize(pxToRem(15), Category.PARAGRAPH)}
                                  fontWeight={400}
                                  _hover={{ cursor: 'pointer' }}
                                  color="mono10"
                                  textDecoration={'underline'}
                                  onClick={handleCoupleNoteOpen}
                                  typographyVariant="body1"
                                  lineHeight={1.5}
                                >
                                  {isCoupleNoteOpen ? `${accTrans.less()}` : `${accTrans.more()}`}
                                </TextV2>
                              </GuestSiteTypographyOverride>
                            )}
                          </Flex>
                        )}

                        {cutoffDate && isGridView && (
                          <GuestSiteTypographyOverride override={body1ToParagraphOverride} defaultFontFamily="Inter UI">
                            <TextV2
                              typographyVariant={'body1'}
                              width="100%"
                              {...(!fontPackExperimentEnabled && { fontFamily: 'Inter UI' })}
                              fontSize={scaleFontSize(pxToRem(15), Category.PARAGRAPH)}
                              color="accentGreen3"
                              fontWeight={400}
                              lineHeight={1.5}
                              letterSpacing="-0.15px"
                            >
                              {accTrans.beforeDate({
                                date: createDateFromUnformattedString(cutoffDate)?.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
                              })}
                            </TextV2>
                          </GuestSiteTypographyOverride>
                        )}
                      </Box>
                    )}
                    {/* PRICING FOR HOTEL (ENABLED WITH EXPERIMENT TREATMENT) */}
                    {type === AccommodationCombinedType.Hotel && isGridView && showRegularHotelPricing ? (
                      showNewPriceComparisonUI ? (
                        <JoyPlacePricingV2
                          width={description ? '50%' : '100%'}
                          minWidth={pxToRem(288)}
                          accommodation={accommodation}
                          locationInfo={locationInfo}
                          isGridView={true}
                        />
                      ) : (
                        <JoyPlacePricing width={description ? '50%' : '100%'} minWidth={pxToRem(288)} accommodation={accommodation} isGridView={true} isFromTiles={true} />
                      )
                    ) : null}
                    {/* BEGIN ROOM BLOCK FOR GRID VIEW, IS ON THE RIGHT SIDE OF DETAILS */}
                    {type === AccommodationCombinedType.Roomblock && pricePerNight && isGridView && (
                      <RoomBlockPricingForGrid width={description || cutoffDate ? '50%' : '100%'} minWidth="162px" accommodation={accommodation} />
                    )}
                    {/* END ROOM BLOCK FOR GRID VIEW */}
                  </Flex>
                </Flex>
                {/* BEGIN ROOM BLOCK PRICING */}
                {type === AccommodationCombinedType.Roomblock && pricePerNight && !isGridView ? <RoomBlockPricingNonGrid accommodation={accommodation} /> : null}
                {/* END ROOM BLOCK PRICING */}

                {/* PRICING FOR HOTEL (ENABLED WITH EXPERIMENT TREATMENT) */}
                {type === AccommodationCombinedType.Hotel && showRegularHotelPricing && !isGridView ? (
                  showNewPriceComparisonUI ? (
                    <JoyPlacePricingV2 width={'100%'} accommodation={accommodation} locationInfo={locationInfo} isGridView={isGridView} />
                  ) : (
                    <JoyPlacePricing width={'100%'} accommodation={accommodation} isGridView={isGridView} isLargeTile={!isMobile} isFromTiles={true} />
                  )
                ) : null}
              </Flex>

              {/* ALOHA (NOT GRID VIEW) SHOWS BUTTON AT BOTTOM */}
              {hasLink && <CTAButton ctaTitle={ctaTitle} width={isMobile ? { _: '100%', sm2: isBrannan ? pxToRem(278) : '100%' } : '100%'} variant="solid" />}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
});

const AccommodationsTileV2FavoriteSkeleton: React.FC = () => {
  const { layout } = useLayout();
  const isBrannan = layout === 'brannan';
  const isMobile = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'sm2' }));
  const isGridView = isBrannan && !isMobile;

  return (
    <Flex width={'100%'} padding={6} borderRadius={pxToRem(isGridView ? 24 : 12)} flexDirection="column" gap={6}>
      {/* Image */}
      <SkeletonThumbnail
        height={pxToRem(isGridView ? 324 : 206)}
        overrides={{
          PlaceholderRoot: {
            props: {
              width: '100%',
              backgroundColor: 'white',
              borderRadius: isGridView ? '24px !important' : '12px !important'
            }
          }
        }}
      />

      {/* Content */}
      <Flex flexDirection={isGridView ? 'row' : 'column'} gap={6}>
        <Flex flexDirection="column" gap={4} flex={1}>
          {/* Hotel name */}
          <SkeletonText rows={1} width="70%" skeletonHeight={pxToRem(24)} />

          {/* Distance */}
          <SkeletonText rows={1} width="40%" skeletonHeight={pxToRem(16)} />

          {/* Description */}
          <Box>
            <SkeletonText rows={1} width="100%" skeletonHeight={pxToRem(16)} paddingBottom={2} />
            <SkeletonText rows={1} width="80%" skeletonHeight={pxToRem(16)} />
          </Box>

          {/* Pricing for non-grid */}
          {!isGridView && (
            <Flex justifyContent="space-between" alignItems="center">
              <SkeletonText rows={1} width="30%" skeletonHeight={pxToRem(16)} />
              <SkeletonText rows={1} width="25%" skeletonHeight={pxToRem(20)} />
            </Flex>
          )}

          {/* Button for non-grid */}
          {!isGridView && <SkeletonText rows={1} width="100%" skeletonHeight={pxToRem(44)} />}
        </Flex>

        {/* Pricing for grid view */}
        {isGridView && (
          <Flex flexDirection="column" gap={2} minWidth="172px" alignItems="flex-end">
            <SkeletonText rows={1} width="80%" skeletonHeight={pxToRem(16)} />
            <SkeletonText rows={1} width="100%" skeletonHeight={pxToRem(20)} />
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

export { AccommodationsTileV2Favorite, AccommodationsTileV2FavoriteSkeleton };

const RoomBlockPricingForGrid = (props: { accommodation: AccommodationCombined } & BoxProps) => {
  const { accommodation, ...boxProps } = props;
  const { strikeoutPricePerNight, pricePerNight } = accommodation;
  const fontPackExperimentEnabled = useFeatureValue('fontPackExperiment').value === 'treatment';
  const { scaleFontSize } = useScaleGuestSiteFontSize();
  const accTrans = useTranslation('guestSiteCustom').t('accommodations');
  return (
    <Flex flexDirection="column" gap={1} {...boxProps}>
      <GuestSiteTypographyOverride override={hed4ToSubHeadingOverride} defaultFontFamily="Inter UI">
        <TextV2 typographyVariant={'body1'} color="accentGreen3" fontWeight={400} textAlign="right">
          {accTrans.groupRate()}
        </TextV2>
      </GuestSiteTypographyOverride>
      <Flex gap={2} alignItems="center" justifyContent="flex-end">
        {!!strikeoutPricePerNight && (
          <GuestSiteTypographyOverride override={hed4ToSubHeadingOverride} defaultFontFamily="Inter UI">
            <TextV2
              {...(!fontPackExperimentEnabled && { fontFamily: 'Inter UI' })}
              color="mono10"
              fontSize={scaleFontSize(pxToRem(17), Category.SUB_HEADING)}
              letterSpacing={'-0.002px'}
              typographyVariant="body3"
              fontWeight={400}
              backgroundSize={'100% 1.5px'}
              width={'auto'}
              backgroundImage={'linear-gradient(currentColor, currentColor)'}
              backgroundPosition={'0% 50%'}
              backgroundRepeat={'no-repeat'}
              lineHeight={1.6}
            >
              ${strikeoutPricePerNight}
            </TextV2>
          </GuestSiteTypographyOverride>
        )}
        <GuestSiteTypographyOverride override={hed4ToSubHeadingOverride} defaultFontFamily="Inter UI">
          <Box
            display={'flex'}
            typographyVariant={'hed4'}
            alignItems={'center'}
            justifyContent={'flex-end'}
            color="mono14"
            fontWeight={600}
            letterSpacing={'-0.02em'}
            textTransform="none"
            lineHeight={1.3}
          >
            ${accTrans.pricePerNight({ price: pricePerNight })}
          </Box>
        </GuestSiteTypographyOverride>
      </Flex>
    </Flex>
  );
};
